import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import { StateManager } from '../../utilities/stateManager.js';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import firebase from '../../utilities/firebase.js';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import RequestManager from '../../utilities/requestManager.js';
import SignatureStatusDialog from '../../components/CarModal/SignatureStatusDialog';
import InPersonSignDialog from '../../components/CarModal/InPersonSignDialog';
import SendServiceDocumentDialog from '../../components/SendServiceDocumentDialog';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Autocomplete from '@mui/material/Autocomplete';
import AddServiceOrderDialog from '../../components/AddServiceOrderDialog';
import OrderPartDialog from '../../components/OrderPartDialog';
import DuplicatesDialog from './DuplicatesDialog';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import constants from '../../utilities/constants';
import moment from 'moment';
import { computeEstimateTotals } from '../../utilities/estimateTotals';
const db = firebase.firestore();

export default function Estimate({ order }) {
  const [selectedServices, setSelectedServices] = useState(order.services || []);
  const [selectedParts, setSelectedParts] = useState(order.parts || []);
  const [selectedSubcontracts, setSelectedSubcontracts] = useState(order.subcontracts || []);
  const [partMargins, setPartMargins] = useState({});
  const [subcontractMargins, setSubcontractMargins] = useState({});
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  const [taxEnabled, setTaxEnabled] = useState(order.taxEnabled === false ? false : true);
  const [invalidMargins, setInvalidMargins] = useState(false);
  const [shopSuppliesFee, setShopSuppliesFee] = useState(
    order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" ? 0 : order.shopSuppliesFee || 0
  );
  const [wasteFee, setWasteFee] = useState(
    order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" ? 0 : isValidNumber(order.wasteFee) ? order.wasteFee : 50
  );
  const [discount, setDiscount] = useState(order.discount || 0);
  const [flatServiceTotal, setFlatServiceTotal] = useState(
    order.flatServiceTotal !== undefined ? order.flatServiceTotal : null
  );
  const [flatPartsTotal, setFlatPartsTotal] = useState(
    order.flatPartsTotal !== undefined ? order.flatPartsTotal : null
  );
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editingPartId, setEditingPartId] = useState(null);
  const [editedServiceNames, setEditedServiceNames] = useState({});
  const [editedPartNames, setEditedPartNames] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isGeneratingEstimate, setIsGeneratingEstimate] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [inPersonOpen, setInPersonOpen] = useState(false);
  const [inPersonUrl, setInPersonUrl] = useState('');
  const [inPersonSigner, setInPersonSigner] = useState('');
  const [sendDocOpen, setSendDocOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [laborRate, setLaborRate] = useState(
    order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" ? 100 : (order.laborRate || 150)
  );
  const [shopSuppliesRate, setShopSuppliesRate] = useState(
    order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" ? 0 : isValidNumber(order.shopSuppliesRate) ? order.shopSuppliesRate : 0.03
  );
  const [documentType, setDocumentType] = useState(order.documentType || 'estimate');
  // Group services by tag both on screen and on the generated document. Default on.
  const [groupServicesByTag, setGroupServicesByTag] = useState(order.groupServicesByTag !== false);
  const [editingTagId, setEditingTagId] = useState(null);
  // Parts grouping mirrors services, but defaults OFF so existing orders keep their
  // flat parts list until someone opts in.
  const [groupPartsByTag, setGroupPartsByTag] = useState(order.groupPartsByTag === true);
  const [editingPartTagId, setEditingPartTagId] = useState(null);
  
  // Add dialog state variables
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [addPartOpen, setAddPartOpen] = useState(false);

  // AI duplicate detection
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  
  const DEFAULT_MARGIN = order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" ? 0 : 35; // 0% margin for specific customer, 35% default margin for others
  const TAX_RATE = order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" ? 0 : 0.06;

  // Initialize selections and margins when order data changes
  useEffect(() => {
    if (order.services) {
      const selectedIds = order.selectedServiceIds || order.services.map(s => s.id);
      // Excluded services never count toward the estimate, even if still selected.
      setSelectedServices(order.services.filter(s => selectedIds.includes(s.id) && !isExcludedService(s)));
    }

    if (order.parts) {
      const selectedIds = order.selectedPartIds || order.parts.map(p => p.id);
      // Excluded parts never count toward the estimate, even if still selected.
      setSelectedParts(order.parts.filter(p => selectedIds.includes(p.id) && !isExcludedPart(p)));
      
      const initialMargins = {};
      order.parts.forEach(part => {
        if (part.margin === null || part.margin === undefined || part.margin === '') {
          part.margin = DEFAULT_MARGIN
        }
        initialMargins[part.id] = part.margin;
      });
      setPartMargins(initialMargins);

      // Set default fees only if they're currently blank
      if (shopSuppliesFee === null || shopSuppliesFee === undefined || shopSuppliesFee === '') {
        const initialSubtotal = calculateInitialSubtotal(
          (order.services || []).filter(s => !isExcludedService(s)),
          order.parts.filter(p => !isExcludedPart(p)),
          initialMargins
        );
        setShopSuppliesFee(initialSubtotal * shopSuppliesRate);
      }
      if (wasteFee === null || wasteFee === undefined || wasteFee === '') {
        setWasteFee(50);
      }
    }

    if (order.subcontracts) {
      const selectedIds = order.selectedSubcontractIds || order.subcontracts.map(s => s.id);
      // Declined subcontracts never count toward the estimate, even if selected.
      setSelectedSubcontracts(order.subcontracts.filter(s => selectedIds.includes(s.id) && !s.declined));

      const initialSubMargins = {};
      order.subcontracts.forEach(sub => {
        if (sub.margin === null || sub.margin === undefined || sub.margin === '') {
          sub.margin = DEFAULT_MARGIN;
        }
        initialSubMargins[sub.id] = sub.margin;
      });
      setSubcontractMargins(initialSubMargins);
    }
  }, [order.services, order.parts, order.subcontracts, order.selectedServiceIds, order.selectedPartIds, order.selectedSubcontractIds]);

  // Add new useEffect to calculate totals after state updates
  useEffect(() => {
    calculateTotals();
  }, [selectedServices, selectedParts, partMargins, shopSuppliesFee, wasteFee, discount, taxEnabled]);

  // Add useEffect to update totals when rates change
  useEffect(() => {
    calculateTotals();
  }, [selectedServices, selectedParts, partMargins, shopSuppliesFee, wasteFee, discount, taxEnabled, laborRate, shopSuppliesRate]);

  const handleMarginChange = async (partId, newValue) => {
    const newMargins = {
      ...partMargins,
      [partId]: newValue // Store the raw value, even if empty
    };
    setPartMargins(newMargins);
    
    // Check if any margins are invalid
    const hasInvalidMargins = order.parts?.some(part => {
      const margin = newMargins[part.id];
      return margin === null || margin === undefined || margin === '';
    });
    setInvalidMargins(hasInvalidMargins);

    const numericValue = Number(newValue);
    console.log(partId, numericValue);
    // Only proceed if it's a valid number (including zero)
    if (numericValue >= 0) {
      try {
        // Update Firebase record
        await db.doc(`parts/${partId}`).update({ margin: numericValue });
        
        // Update order object
        const updatedParts = order.parts.map(part => 
          part.id === partId ? { ...part, margin: numericValue } : part
        );
        StateManager.updateOrder({ parts: updatedParts });
      } catch (error) {
        console.error('Error updating margin:', error);
        setSnackbar({
          open: true,
          message: `Failed to update margin: ${error.message}`,
          severity: 'error'
        });
      }
    }

    // Recalculate totals with new margins
    calculateTotals();
  };

  const calculatePartPrice = (part) => {
    const cost = part.cost || 0;
    const margin = partMargins[part.id];
    
    if (margin === null || margin === undefined || margin === '') {
      return 0;
    }
    
    return cost * (1 + margin / 100);
  };

  const calculateSubcontractPrice = (sub) => {
    const cost = sub.cost || 0;
    const margin = subcontractMargins[sub.id];

    if (margin === null || margin === undefined || margin === '') {
      return 0;
    }

    return cost * (1 + margin / 100);
  };

  const calculateSubcontractTotal = () => {
    return selectedSubcontracts.reduce((sum, sub) => {
      const margin = isValidNumber(subcontractMargins[sub.id]) ? subcontractMargins[sub.id] : DEFAULT_MARGIN;
      return sum + (sub.cost || 0) * (1 + margin / 100);
    }, 0) || 0;
  };

  const calculateAdjustedSubtotal = () => {
    return subtotal + shopSuppliesFee + wasteFee - discount;
  };

  const calculateTotalPayments = () => {
    return (order.deposits || [])
      .filter(deposit => deposit.type === 'service')
      .reduce((sum, deposit) => sum + (deposit.amount || 0), 0);
  };

  const calculateInitialSubtotal = (services, expenses, margins) => {
    const servicesTotal = services?.reduce((sum, service) => {
      const hours = service.time || 1;
      return sum + (hours * laborRate);
    }, 0) || 0;
    
    const partsTotal = expenses?.reduce((sum, part) => {
      const margin = margins[part.id] || DEFAULT_MARGIN;
      const markupMultiplier = 1 + (margin / 100);
      const quantity = part.quantity || 1;
      return sum + (part.cost * markupMultiplier * quantity);
    }, 0) || 0;
    
    return servicesTotal + partsTotal;
  };

  const calculateTotals = () => {
    const servicesTotal = selectedServices?.reduce((sum, service) => {
      const hours = service.time || 1;
      return sum + (hours * laborRate);
    }, 0) || 0;
    
    const partsTotal = selectedParts.reduce((sum, part) => {
      const margin = isValidNumber(partMargins[part.id]) ? partMargins[part.id] : DEFAULT_MARGIN;
      const markupMultiplier = 1 + (margin / 100);
      const quantity = part.quantity || 1;
      return sum + (part.cost * markupMultiplier * quantity);
    }, 0) || 0;
    
    const newSubtotal = servicesTotal + partsTotal;
    const newShopSuppliesFee = newSubtotal * shopSuppliesRate;
    const adjustedSubtotal = newSubtotal + newShopSuppliesFee + wasteFee - discount;
    
    // Only apply tax if there are parts selected
    const shouldApplyTax = selectedParts.length > 0;
    const newTax = (taxEnabled && shouldApplyTax) ? adjustedSubtotal * TAX_RATE : 0;
    
    setSubtotal(newSubtotal);
    setShopSuppliesFee(newShopSuppliesFee);
    setTax(newTax);
    setTotal(adjustedSubtotal + newTax);
  };

  const handleServiceToggle = async (service) => {
    if (service.declined) return; // declined services can't be added to the estimate
    try {
      const isSelected = selectedServices.some(s => s.id === service.id);
      const newServices = isSelected 
        ? selectedServices.filter(s => s.id !== service.id)
        : [...selectedServices, service];

      // Update local state
      setSelectedServices(newServices);

      // Update Firebase
      const serviceIds = newServices.map(s => s.id);
      await db.doc(`orders/${order.stock}`).update({ 
        selectedServiceIds: serviceIds 
      });
      
      // Update order in state manager
      StateManager.updateOrder({ selectedServiceIds: serviceIds });

      // Calculate totals...
      const servicesTotal = newServices.reduce((sum, s) => {
        const hours = s.time || 0;
        return sum + (hours * laborRate);
      }, 0);
      
      const partsTotal = selectedParts.reduce((sum, part) => {
        const margin = isValidNumber(partMargins[part.id]) ? partMargins[part.id] : DEFAULT_MARGIN;
        const markupMultiplier = 1 + (margin / 100);
        const quantity = part.quantity || 1;
        return sum + (part.cost * markupMultiplier * quantity);
      }, 0) || 0;
      
      const newSubtotal = servicesTotal + partsTotal;
      const adjustedSubtotal = newSubtotal + shopSuppliesFee + wasteFee - discount;
      const newTax = taxEnabled ? adjustedSubtotal * TAX_RATE : 0;
      
      setSubtotal(newSubtotal);
      setTax(newTax);
      setTotal(adjustedSubtotal + newTax);
      
    } catch (error) {
      console.error('Error updating selected services:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update service selection: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handlePartToggle = async (part) => {
    if (isExcludedPart(part)) return; // excluded parts can't be added to the estimate
    try {
      const isSelected = selectedParts.some(p => p.id === part.id);
      const newParts = isSelected 
        ? selectedParts.filter(p => p.id !== part.id)
        : [...selectedParts, part];

      // Update local state
      setSelectedParts(newParts);

      // Update Firebase
      const partIds = newParts.map(p => p.id);
      await db.doc(`orders/${order.stock}`).update({ 
        selectedPartIds: partIds 
      });
      
      // Update order in state manager
      StateManager.updateOrder({ selectedPartIds: partIds });

      // Calculate totals...
      const servicesTotal = selectedServices.reduce((sum, service) => {
        const hours = service.time || 1;
        return sum + (hours * laborRate);
      }, 0);
      
      const partsTotal = newParts.reduce((sum, part) => {
        const margin = isValidNumber(partMargins[part.id]) ? partMargins[part.id] : DEFAULT_MARGIN;
        const markupMultiplier = 1 + (margin / 100);
        const quantity = part.quantity || 1;
        return sum + (part.cost * markupMultiplier * quantity);
      }, 0) || 0;
      
      const newSubtotal = servicesTotal + partsTotal;
      const adjustedSubtotal = newSubtotal + shopSuppliesFee + wasteFee - discount;
      const newTax = taxEnabled ? adjustedSubtotal * TAX_RATE : 0;
      
      setSubtotal(newSubtotal);
      setTax(newTax);
      setTotal(adjustedSubtotal + newTax);
      
    } catch (error) {
      console.error('Error updating selected parts:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update part selection: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleSubcontractToggle = async (sub) => {
    if (sub.declined) return; // declined subcontracts can't be added to the estimate
    try {
      const isSelected = selectedSubcontracts.some(s => s.id === sub.id);
      const newSubs = isSelected
        ? selectedSubcontracts.filter(s => s.id !== sub.id)
        : [...selectedSubcontracts, sub];

      setSelectedSubcontracts(newSubs);

      const subIds = newSubs.map(s => s.id);
      await db.doc(`orders/${order.stock}`).update({ selectedSubcontractIds: subIds });
      StateManager.updateOrder({ selectedSubcontractIds: subIds });
    } catch (error) {
      console.error('Error updating selected subcontracts:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update subcontract selection: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleSubcontractMarginChange = async (subId, newValue) => {
    setSubcontractMargins(prev => ({ ...prev, [subId]: newValue }));

    const numericValue = Number(newValue);
    if (numericValue >= 0 && newValue !== '') {
      try {
        await db.doc(`subcontracts/${subId}`).update({ margin: numericValue });
        const updated = (order.subcontracts || []).map(sub =>
          sub.id === subId ? { ...sub, margin: numericValue } : sub
        );
        StateManager.updateOrder({ subcontracts: updated });
      } catch (error) {
        console.error('Error updating subcontract margin:', error);
        setSnackbar({
          open: true,
          message: `Failed to update margin: ${error.message}`,
          severity: 'error'
        });
      }
    }
  };

  // Add this helper function for input formatting
  const formatInputValue = (value) => {
    // Remove leading zeros and return empty string if 0
    if (value === 0) return '';
    return value.toString();
  };

  const handleFieldEdit = async (type, id, newValue) => {
    const config = {
      service: {
        collection: 'services',
        field: 'name',
        orderField: 'services',
        setState: setEditedServiceNames,
        displayName: 'service description'
      },
      part: {
        collection: 'parts',
        field: 'name',
        orderField: 'parts',
        setState: setEditedPartNames,
        displayName: 'part description'
      }
    };

    const { collection, field, orderField, setState, displayName } = config[type];

    // Update local state
    setState(prev => ({
      ...prev,
      [id]: newValue
    }));

    try {
      // Update Firebase record using db
      await db.doc(`${collection}/${id}`).update({ [field]: newValue });
      
      // Update order object
      const updatedItems = order[orderField].map(item => 
        item.id === id ? { ...item, [field]: newValue } : item
      );
      StateManager.updateOrder({[orderField]: updatedItems});

    } catch (error) {
      console.error(`Error updating ${type}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to update ${displayName}: ${error.message}`,
        severity: 'error'
      });
      
      setState(prev => ({
        ...prev,
        [id]: order[orderField].find(item => item.id === id)?.[field]
      }));
    }
  };

  // Simplified handlers that use the shared function
  const handleServiceNameEdit = (serviceId, newName) => {
    handleFieldEdit('service', serviceId, newName);
  };

  // Update a single service's tag (used by the inline group editor). Falls back to 'Other'.
  const handleServiceTagEdit = async (serviceId, rawTag) => {
    const tag = (rawTag || '').trim() || 'Other';
    const current = (order.services || []).find(s => s.id === serviceId)?.tag || '';
    if (tag === current) return;
    try {
      await db.doc(`services/${serviceId}`).update({ tag });
      const updated = (order.services || []).map(s => s.id === serviceId ? { ...s, tag } : s);
      StateManager.updateOrder({ services: updated });
    } catch (error) {
      console.error('Error updating service tag:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update tag: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Toggle service grouping (drives both the on-screen view and the document).
  const handleGroupServicesToggle = async (checked) => {
    setGroupServicesByTag(checked);
    try {
      await db.doc(`orders/${order.stock}`).update({ groupServicesByTag: checked });
      StateManager.updateOrder({ groupServicesByTag: checked });
    } catch (error) {
      console.error('Error updating grouping preference:', error);
    }
  };

  // Update a single part's tag (used by the inline group editor). Falls back to 'Other'.
  const handlePartTagEdit = async (partId, rawTag) => {
    const tag = (rawTag || '').trim() || 'Other';
    const current = (order.parts || []).find(p => p.id === partId)?.tag || '';
    if (tag === current) return;
    try {
      await db.doc(`parts/${partId}`).update({ tag });
      const updated = (order.parts || []).map(p => p.id === partId ? { ...p, tag } : p);
      StateManager.updateOrder({ parts: updated });
    } catch (error) {
      console.error('Error updating part tag:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update tag: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Toggle part grouping (drives both the on-screen view and the document).
  const handleGroupPartsToggle = async (checked) => {
    setGroupPartsByTag(checked);
    try {
      await db.doc(`orders/${order.stock}`).update({ groupPartsByTag: checked });
      StateManager.updateOrder({ groupPartsByTag: checked });
    } catch (error) {
      console.error('Error updating parts grouping preference:', error);
    }
  };

  const handlePartNameEdit = (partId, newName) => {
    handleFieldEdit('part', partId, newName);
  };

  const handleKeyDown = (e, resetFunction) => {
    if (e.key === 'Escape') {
      resetFunction();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDiscountChange = async (value) => {
    try {
      const numericValue = value === '' ? 0 : Number(value);
      setDiscount(numericValue);
      await db.doc(`orders/${order.stock}`).update({ discount: numericValue });
      StateManager.updateOrder({ discount: numericValue });
    } catch (error) {
      console.error('Error updating discount:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update discount: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Add this function to calculate service and parts totals separately
  const calculateServiceTotal = () => {
    return selectedServices?.reduce((sum, service) => {
      const hours = service.time || 1;
      return sum + (hours * laborRate);
    }, 0) || 0;
  };

  const calculatePartsTotal = () => {
    return selectedParts.reduce((sum, part) => {
      const margin = isValidNumber(partMargins[part.id]) ? partMargins[part.id] : DEFAULT_MARGIN;
      const markupMultiplier = 1 + (margin / 100);
      const quantity = part.quantity || 1;
      return sum + (part.cost * markupMultiplier * quantity);
    }, 0) || 0;
  };

  // Declined services and declined/returning/returned parts are hidden from the estimate.
  const orderedServices = React.useMemo(
    () => applyCustomOrder((order.services || []).filter(s => !isExcludedService(s)), order.serviceOrder),
    [order.services, order.serviceOrder]
  );
  const orderedParts = React.useMemo(
    () => applyCustomOrder((order.parts || []).filter(p => !isExcludedPart(p)), order.partOrder),
    [order.parts, order.partOrder]
  );
  const orderedSubcontracts = React.useMemo(
    () => applyCustomOrder(order.subcontracts || [], order.subcontractOrder),
    [order.subcontracts, order.subcontractOrder]
  );

  // Services grouped by tag, preserving the manual order within each group and
  // listing groups in first-seen order. Used for the grouped estimate view.
  const groupedServices = React.useMemo(() => {
    const groups = [];
    const idx = {};
    for (const s of orderedServices) {
      const tag = (s.tag && s.tag.trim()) || 'Other';
      if (idx[tag] === undefined) { idx[tag] = groups.length; groups.push({ tag, services: [] }); }
      groups[idx[tag]].services.push(s);
    }
    return groups;
  }, [orderedServices]);

  // Parts grouped by tag, mirroring groupedServices. Preserves manual order within
  // each group and lists groups in first-seen order.
  const groupedParts = React.useMemo(() => {
    const groups = [];
    const idx = {};
    for (const p of orderedParts) {
      const tag = (p.tag && p.tag.trim()) || 'Other';
      if (idx[tag] === undefined) { idx[tag] = groups.length; groups.push({ tag, parts: [] }); }
      groups[idx[tag]].parts.push(p);
    }
    return groups;
  }, [orderedParts]);

  const persistOrder = async (field, idArray) => {
    StateManager.updateOrder({ [field]: idArray });
    try {
      await db.doc(`orders/${order.stock}`).update({ [field]: idArray });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to save order: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleServiceDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const newOrder = Array.from(orderedServices);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    persistOrder('serviceOrder', newOrder.map(s => s.id));
  };

  const handlePartDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const newOrder = Array.from(orderedParts);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    persistOrder('partOrder', newOrder.map(p => p.id));
  };

  const alphaCompare = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

  const handleSortServicesAlpha = () => {
    const sorted = [...(order.services || [])].sort(alphaCompare);
    persistOrder('serviceOrder', sorted.map(s => s.id));
  };

  const handleSortPartsAlpha = () => {
    const sorted = [...(order.parts || [])].sort(alphaCompare);
    persistOrder('partOrder', sorted.map(p => p.id));
  };

  const renderDragClone = (label, sublabel) => (provided, snapshot, rubric) => createPortal(
    <Paper
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      elevation={6}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.25,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.light',
        zIndex: 20000,
        // Ignore any inline width beautiful-dnd captured from the <tr> — let the clone size to content
        width: 'auto',
      }}
      style={{ ...provided.draggableProps.style, width: 'auto' }}
    >
      <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      <Box sx={{ flex: 1, fontWeight: 500 }}>{label}</Box>
      {sublabel != null && (
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{sublabel}</Box>
      )}
    </Paper>,
    document.body,
  );

  const renderServiceClone = (provided, snapshot, rubric) => {
    const service = orderedServices[rubric.source.index];
    if (!service) return null;
    const hours = service.time || 1;
    return renderDragClone(
      service.name || '(unnamed service)',
      `${hours} hr · $${(hours * laborRate).toFixed(2)}`
    )(provided, snapshot, rubric);
  };

  const renderPartClone = (provided, snapshot, rubric) => {
    const part = orderedParts[rubric.source.index];
    if (!part) return null;
    const qty = part.quantity || 1;
    return renderDragClone(
      part.name || '(unnamed part)',
      `qty ${qty} · $${(part.cost || 0).toFixed(2)}`
    )(provided, snapshot, rubric);
  };

  // Add this function to handle estimate generation
  // Ask the AI to flag duplicate services/parts, then open the review dialog.
  const handleFindDuplicates = async () => {
    setIsCheckingDuplicates(true);
    try {
      const services = (order.services || [])
        .filter(s => !s.declined)
        .map(s => ({ id: s.id, name: s.name, tag: s.tag, time: s.time }));
      const parts = (order.parts || [])
        .filter(p => !isExcludedPart(p))
        .map(p => ({ id: p.id, name: p.name || p.memo, tag: p.tag, quantity: p.quantity, cost: p.cost }));

      if (services.length < 2 && parts.length < 2) {
        setDuplicateGroups([]);
        setDuplicatesOpen(true);
        return;
      }

      const result = await RequestManager.post({
        function: 'detectDuplicateEstimateItems',
        variables: { services, parts },
      });

      if (!result || result.success === false) {
        throw new Error(result?.error || 'Duplicate check failed');
      }

      setDuplicateGroups(Array.isArray(result.groups) ? result.groups : []);
      setDuplicatesOpen(true);
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      setSnackbar({
        open: true,
        message: `Failed to check for duplicates: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  // Assemble the estimate/invoice payload. Shared by document generation and the
  // e-signature pipeline (createSignatureSession builds the same PDF server-side).
  const buildEstimateData = () => {
      const serviceTotal = calculateServiceTotal();
      const partsTotal = calculatePartsTotal();
      const subcontractTotal = calculateSubcontractTotal();

      const isFlatInvoice = documentType === 'flat-invoice';
      const genServiceTotal = isFlatInvoice ? effectiveFlatServiceTotal : serviceTotal;
      const genPartsTotal = isFlatInvoice ? effectiveFlatPartsTotal : partsTotal;
      const genSubcontractTotal = isFlatInvoice ? effectiveFlatSubcontractTotal : subcontractTotal;
      const genSubtotal = isFlatInvoice ? effectiveFlatSubtotal : subtotal;

      // Format parts data
      const selectedPartIdSet = new Set(selectedParts.map(p => p.id));
      const partsData = orderedParts.filter(p => selectedPartIdSet.has(p.id)).map((part, index) => {
        const margin = isValidNumber(partMargins[part.id]) ? partMargins[part.id] : DEFAULT_MARGIN;
        const markupMultiplier = 1 + (margin / 100);
        const quantity = part.quantity || 1;
        const charge = part.cost * markupMultiplier * quantity;

        return {
          item: `item ${index + 1}`,
          description: part.memo || part.name,
          tag: (part.tag && part.tag.trim()) || 'Other',
          // Flat invoice: list every part with no per-row charge — the lump
          // shows only as PARTS TOTAL in the summary. Non-flat shows per-row.
          ...(isFlatInvoice ? {} : { charge }),
        };
      });

      // Format labor data
      const selectedServiceIdSet = new Set(selectedServices.map(s => s.id));
      const laborData = orderedServices.filter(s => selectedServiceIdSet.has(s.id)).map((service, index) => {
        const hours = service.time || 1;
        return {
          item: `item ${index + 1}`,
          description: service.name,
          tag: (service.tag && service.tag.trim()) || 'Other',
          ...(isFlatInvoice
            // Flat + grouped: list services under tag headers with no per-row
            // charges (the single SERVICE TOTAL shows in the summary). Flat +
            // ungrouped keeps the lump on the first line as before.
            ? (groupServicesByTag ? {} : (index === 0 ? { charge: genServiceTotal } : {}))
            : { charge: hours * laborRate }),
        };
      });

      // Format subcontracted work data
      const selectedSubcontractIdSet = new Set(selectedSubcontracts.map(s => s.id));
      const subcontractData = orderedSubcontracts.filter(s => selectedSubcontractIdSet.has(s.id)).map((sub, index) => {
        const margin = isValidNumber(subcontractMargins[sub.id]) ? subcontractMargins[sub.id] : DEFAULT_MARGIN;
        const charge = (sub.cost || 0) * (1 + margin / 100);
        return {
          item: `item ${index + 1}`,
          description: sub.name,
          ...(isFlatInvoice
            ? (index === 0 ? { charge: genSubcontractTotal } : {})
            : { charge }),
        };
      });

      // Format customer data
      const customerData = {
        name: (order.customer.first_name || "") + (order.customer.first_name ? " " : "")+ (order.customer.last_name || ""),
        address: order.customer.address1,
        cityState: (order.customer.city && order.customer.state) ? `${order.customer.city}, ${order.customer.state}` : false,
        phone: order.customer.phone_number,
        email: order.customer.email
      };

      // Format vehicle data
      const vehicleData = {
        model: order.car.year + ' ' + order.car.make + ' ' + order.car.model,
        vin: order.car.vin,
        miles: order.car.miles || 'EXEMPT',
        license: order.car.license || ''
      };

      return {
        stock: order.stock,
        date: order.date ? moment(order.date).format("MM/DD/YYYY") : new Date().toLocaleDateString(),
        customer: customerData,
        vehicle: vehicleData,
        parts: partsData,
        labor: laborData,
        subcontract: subcontractData,
        serviceTotal: genServiceTotal,
        partsTotal: genPartsTotal,
        subcontractTotal: genSubcontractTotal,
        subtotal: genSubtotal,
        discount: discount,
        // Match the Estimate tab / accounting mirror: tax only applies when
        // parts are on the document. Without this gate a labor-only order shows
        // $0 tax in the UI but the PDF would still be taxed.
        taxRate: (taxEnabled && selectedParts.length > 0) ? TAX_RATE * 100 : 0,
        shopSupplies: Number(shopSuppliesFee.toFixed(2)),
        wasteFee: Number(wasteFee.toFixed(2)),
        payments: calculateTotalPayments(),
        type: documentType,
        // Group services by tag on the document when the toggle is on. Flat
        // invoices group too, but without per-tag subtotals (no per-line charges).
        groupServicesByTag: groupServicesByTag,
        // Same for parts — the backend renders parts under tag subheaders.
        groupPartsByTag: groupPartsByTag,
      };
  };

  const handleGenerateEstimate = async () => {
    setIsGeneratingEstimate(true);
    try {
      const estimateData = buildEstimateData();
      const response = await RequestManager.post({ function: 'generateServiceInvoice', variables: estimateData });
      if (response.url) window.open(response.url, '_blank');
      else setSnackbar({ open: true, message: `Failed to generate ${documentType}`, severity: 'error' });
    } catch (error) {
      console.error(`Error generating ${documentType}:`, error);
      setSnackbar({ open: true, message: `Error generating ${documentType}: ` + error.message, severity: 'error' });
    } finally {
      setIsGeneratingEstimate(false);
    }
  };

  // ── Service estimate/invoice e-signature ──────────────────────────────────
  const buildServiceSigners = () => (order.customer?.email
    ? [{ role: 'customer', email: order.customer.email, name: `${order.customer.first_name || ''}${order.customer.first_name ? ' ' : ''}${order.customer.last_name || ''}`.trim() || order.customer.email }]
    : []);

  const docLabel = () => (documentType === 'estimate' ? 'Estimate' : 'Invoice');
  const documentTypeLabel = documentType === 'estimate' ? 'Estimate'
    : documentType === 'flat-invoice' ? 'Flat Invoice' : 'Invoice';

  const serviceSessionVariables = (inPerson) => ({
    docType: 'service',
    refId: order.stock,
    signers: buildServiceSigners(),
    inPerson,
    name: `${order.stock} ${docLabel()}`,
    subject: 'Signature Requested - Skyway Classics',
    estimateData: buildEstimateData(),
    // Attribution for the send record. The bearer token on the request is what
    // the backend actually trusts; this is the fallback.
    sentById: StateManager.userID,
  });

  const sendServiceForSignature = async () => {
    if (!buildServiceSigners().length) return StateManager.setAlertAndOpen('Add a customer email to the service order first.', 'error');
    setIsGeneratingEstimate(true);
    const response = await RequestManager.post({ function: 'createSignatureSession', variables: serviceSessionVariables(false) });
    setIsGeneratingEstimate(false);
    if (response.status === 'complete') StateManager.setAlertAndOpen(`${docLabel()} sent for signature!`, 'success');
    else StateManager.setAlertAndOpen(response.message || 'Hmm... there was an error.', 'error');
  };

  const signServiceInPerson = async () => {
    const list = buildServiceSigners();
    if (!list.length) return StateManager.setAlertAndOpen('Add a customer first.', 'error');
    setIsGeneratingEstimate(true);
    const response = await RequestManager.post({ function: 'createSignatureSession', variables: serviceSessionVariables(true) });
    setIsGeneratingEstimate(false);
    if (response.status === 'complete') {
      setInPersonUrl(response.signUrl);
      setInPersonSigner(list[0].name);
      setInPersonOpen(true);
    } else StateManager.setAlertAndOpen(response.message || 'Could not start in-person signing.', 'error');
  };

  // Email the document with no signature request — the customer just needs to
  // read it (an approval quote, a copy for their records, an invoice to pay).
  // Returns true on success so the dialog knows whether to close.
  const emailServiceDocument = async ({ to, cc, subject, message }) => {
    try {
      const response = await RequestManager.post({
        function: 'emailServiceDocument',
        variables: { to, cc, subject, message, estimateData: buildEstimateData(), sentById: StateManager.userID },
      });
      if (response && response.status === 'complete') {
        StateManager.setAlertAndOpen(`${docLabel()} emailed to ${(response.sentTo || to).join(', ')}`, 'success');
        return true;
      }
      StateManager.setAlertAndOpen((response && response.message) || `Could not email the ${docLabel().toLowerCase()}.`, 'error');
      return false;
    } catch (error) {
      StateManager.setAlertAndOpen(`Could not email the ${docLabel().toLowerCase()}: ${error.message}`, 'error');
      return false;
    }
  };

  // Build this estimate with its real data and open it with the signature fields
  // stamped, so placement can be verified without starting a signing session.
  const previewServicePlacement = async () => {
    setIsGeneratingEstimate(true);
    const res = await RequestManager.post({ function: 'previewFieldMap', variables: { docType: 'service', refId: order.stock, estimateData: buildEstimateData() } });
    setIsGeneratingEstimate(false);
    if (res && res.status === 'complete' && res.url) window.open(res.url, '_blank');
    else StateManager.setAlertAndOpen((res && res.message) || 'Could not build the estimate preview.', 'error');
  };

  // Add function to handle document type change
  const handleDocumentTypeChange = async (event, newType) => {
    if (newType !== null) {
      setDocumentType(newType);
      try {
        await db.doc(`orders/${order.stock}`).update({ documentType: newType });
        StateManager.updateOrder({ documentType: newType });
      } catch (error) {
        console.error('Error updating document type:', error);
        setSnackbar({
          open: true,
          message: 'Failed to update document type: ' + error.message,
          severity: 'error'
        });
      }
    }
  };

  // Update handleRateChange to use the correct collection path
  const handleRateChange = async (field, value, defaultValue) => {
    // Only save if the value is different from the default
    try {
      await db.doc(`orders/${order.stock}`).update({ [field]: value });
      StateManager.updateOrder({ [field]: value });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to update ${field}: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Work backwards from a target price: adjust ONLY the labor rate so the estimate
  // total equals the revenue manually entered on the Accounting tab. Parts, fees,
  // discounts and subcontracts are left untouched. The total is affine in laborRate,
  // so we evaluate the canonical estimate math (computeEstimateTotals — the same
  // function the Accounting tab uses to sync revenue) at two rates and interpolate
  // the rate that lands the total on the target. This is the exact inverse of the
  // Accounting tab's "sync revenue from estimate".
  const SKYWAY_ID = "9c0d88f5-84f9-454d-833d-a8ced9adad49";
  const handleSetRateFromRevenue = () => {
    if (order.customer.id === SKYWAY_ID) return; // rate is locked for this customer

    const targetRevenue = Number(order.revenue);
    if (!targetRevenue || targetRevenue <= 0) {
      setSnackbar({
        open: true,
        message: 'Enter a revenue amount on the Accounting tab first.',
        severity: 'warning'
      });
      return;
    }

    // Sample the total at two NONZERO labor rates. (computeEstimateTotals reads the
    // rate as `order.laborRate || 150`, so a 0 here would silently become 150 — both
    // sample rates must be truthy for the slope/intercept to be correct.)
    const r1 = 100;
    const r2 = 1100;
    const t1 = computeEstimateTotals({ ...order, laborRate: r1 });
    const t2 = computeEstimateTotals({ ...order, laborRate: r2 });
    if (!t1 || !t2) {
      setSnackbar({
        open: true,
        message: 'Add at least one service before matching revenue.',
        severity: 'warning'
      });
      return;
    }

    // total is affine in the labor rate: total = slope * rate + intercept.
    // slope = dollars of total per $1 of labor rate; zero when there are no labor
    // hours (or the total does not depend on the rate), so there is nothing to solve.
    const slope = (t2.total - t1.total) / (r2 - r1);
    if (slope <= 0) {
      setSnackbar({
        open: true,
        message: 'This estimate has no labor hours to adjust — add a service with hours.',
        severity: 'warning'
      });
      return;
    }
    const intercept = t1.total - slope * r1;

    const solved = (targetRevenue - intercept) / slope;
    if (!isFinite(solved) || solved < 0) {
      setSnackbar({
        open: true,
        message: `Revenue of $${targetRevenue.toFixed(2)} is below the non-labor costs — can't reach it with the labor rate alone.`,
        severity: 'warning'
      });
      return;
    }

    const newRate = Math.round(solved * 100) / 100;
    setLaborRate(newRate);
    handleRateChange('laborRate', newRate, 150);
    setSnackbar({
      open: true,
      message: `Labor rate set to $${newRate.toFixed(2)} to match revenue of $${targetRevenue.toFixed(2)}.`,
      severity: 'success'
    });
  };

  // Add functions to handle adding services and parts
  // The Add Service dialog now hands off a request descriptor and closes
  // immediately; the create runs here. This legacy page reloads to show the
  // result (the modal's ServicesBlade does the optimistic in-place version).
  const updateSO = async ({ description, removeTime, includeInEstimate, estimateParts }) => {
    if (!description) return;
    try {
      const response = await RequestManager.post({
        function: 'createServices',
        variables: {
          serviceOrderId: order.stock,
          description,
          removeTime,
          includeInEstimate,
          estimateParts,
        },
      });
      if (!response.success) throw new Error(response.error || 'Failed to add service');
      StateManager.setAlertAndOpen('Service added successfully', 'success');
      window.location.reload();
    } catch (error) {
      console.error('Error adding service:', error);
      StateManager.setAlertAndOpen(error.message || 'Error adding service', 'error');
    }
  };

  const handlePartAdded = async () => {
    // Only proceed if we have a stock number
    if (!order.stock) return;
    
    // Refresh the parts data from the database
    try {
      const snapshot = await firebase.firestore()
        .collection('parts')
        .where('order', '==', order.stock)
        .get();

      if (!snapshot.empty) {
        const partsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Update order parts
        StateManager.updateOrder({ parts: partsData });
        
        // Refresh the page to show new parts
        window.location.reload();
      }
    } catch (error) {
      console.error('Error refreshing parts:', error);
      setSnackbar({
        open: true,
        message: 'Error refreshing parts data',
        severity: 'error'
      });
    }
  };

  const handleFlatTotalChange = async (field, value, setter) => {
    const numericValue = value === '' ? null : Number(value);
    setter(numericValue);
    try {
      await db.doc(`orders/${order.stock}`).update({ [field]: numericValue !== null ? numericValue : 0 });
      StateManager.updateOrder({ [field]: numericValue });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setSnackbar({ open: true, message: `Failed to update ${field}: ${error.message}`, severity: 'error' });
    }
  };

  // Effective values used in flat invoice mode
  const effectiveFlatServiceTotal = flatServiceTotal !== null ? flatServiceTotal : calculateServiceTotal();
  const effectiveFlatPartsTotal = flatPartsTotal !== null ? flatPartsTotal : calculatePartsTotal();
  // Subcontracted work isn't given its own flat-rate override field; it always
  // uses the marked-up total. It's non-taxable, so it stays out of the taxable
  // subtotal and is added back into the final total.
  const effectiveFlatSubcontractTotal = calculateSubcontractTotal();
  const effectiveFlatSubtotal = effectiveFlatServiceTotal + effectiveFlatPartsTotal;
  const flatAdjustedSubtotal = effectiveFlatSubtotal + shopSuppliesFee + wasteFee - discount;
  const flatTax = (taxEnabled && selectedParts.length > 0) ? flatAdjustedSubtotal * TAX_RATE : 0;
  const flatTotal = flatAdjustedSubtotal + flatTax + effectiveFlatSubcontractTotal;

  // The cells of a service row after the drag-handle column: Include, Description,
  // Group (inline-editable tag), Hours, Rate, Total. Shared by the grouped and flat views.
  const renderServiceRowCells = (service) => (
    <>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedServices.some(s => s.id === service.id)}
          onChange={() => handleServiceToggle(service)}
        />
      </TableCell>
      <TableCell>
        {editingServiceId === service.id ? (
          <TextField
            fullWidth
            value={editedServiceNames[service.id] || service.name}
            onChange={(e) => handleServiceNameEdit(service.id, e.target.value)}
            onBlur={() => setEditingServiceId(null)}
            onKeyDown={(e) => handleKeyDown(e, () => setEditingServiceId(null))}
            error={!editedServiceNames[service.id] && !service.name}
            helperText={(!editedServiceNames[service.id] && !service.name) ? "Description cannot be empty" : ""}
            autoFocus
          />
        ) : (
          <Box
            onClick={() => {
              setEditingServiceId(service.id);
              if (!editedServiceNames[service.id]) {
                handleServiceNameEdit(service.id, service.name);
              }
            }}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, p: 1 }}
          >
            {editedServiceNames[service.id] || service.name}
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        {editingTagId === service.id ? (
          <Autocomplete
            freeSolo
            size="small"
            openOnFocus
            options={constants.service_tags}
            value={service.tag || ''}
            onChange={(e, v) => { handleServiceTagEdit(service.id, v); setEditingTagId(null); }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                variant="standard"
                placeholder="Group"
                onBlur={(e) => { handleServiceTagEdit(service.id, e.target.value); setEditingTagId(null); }}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditingTagId(null); }}
              />
            )}
          />
        ) : (
          <Box
            onClick={() => setEditingTagId(service.id)}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, display: 'inline-flex' }}
          >
            <Chip size="small" variant="outlined" label={(service.tag && service.tag.trim()) || 'Other'} />
          </Box>
        )}
      </TableCell>
      <TableCell align="right">{service.time || 1}</TableCell>
      <TableCell align="right">${laborRate.toFixed(2)}</TableCell>
      <TableCell align="right">${((service.time || 1) * laborRate).toFixed(2)}</TableCell>
    </>
  );

  // The cells of a part row after the drag-handle column: Include, Description,
  // Group (inline-editable tag), Quantity, Cost, Margin, Total. Shared by the
  // grouped and flat views.
  const renderPartRowCells = (part) => {
    const cost = part.cost || 0;
    const price = calculatePartPrice(part);
    const quantity = part.quantity || 1;
    return (
      <>
        <TableCell padding="checkbox">
          <Checkbox
            checked={selectedParts.some(p => p.id === part.id)}
            onChange={() => handlePartToggle(part)}
          />
        </TableCell>
        <TableCell>
          {editingPartId === part.id ? (
            <TextField
              fullWidth
              value={editedPartNames[part.id] || part.name}
              onChange={(e) => handlePartNameEdit(part.id, e.target.value)}
              onBlur={() => setEditingPartId(null)}
              onKeyDown={(e) => handleKeyDown(e, () => setEditingPartId(null))}
              error={!editedPartNames[part.id] && !part.name}
              helperText={(!editedPartNames[part.id] && !part.name) ? "Description cannot be empty" : ""}
              autoFocus
            />
          ) : (
            <Box
              onClick={() => {
                setEditingPartId(part.id);
                if (!editedPartNames[part.id]) {
                  handlePartNameEdit(part.id, part.name);
                }
              }}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, p: 1 }}
            >
              {editedPartNames[part.id] || part.name}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ minWidth: 150 }}>
          {editingPartTagId === part.id ? (
            <Autocomplete
              freeSolo
              size="small"
              openOnFocus
              options={constants.service_tags}
              value={part.tag || ''}
              onChange={(e, v) => { handlePartTagEdit(part.id, v); setEditingPartTagId(null); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  variant="standard"
                  placeholder="Group"
                  onBlur={(e) => { handlePartTagEdit(part.id, e.target.value); setEditingPartTagId(null); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') setEditingPartTagId(null); }}
                />
              )}
            />
          ) : (
            <Box
              onClick={() => setEditingPartTagId(part.id)}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, display: 'inline-flex' }}
            >
              <Chip size="small" variant="outlined" label={(part.tag && part.tag.trim()) || 'Other'} />
            </Box>
          )}
        </TableCell>
        <TableCell align="right">{quantity}</TableCell>
        <TableCell align="right">${cost.toFixed(2)}</TableCell>
        <TableCell align="right">
          <TextField
            type="number"
            value={partMargins[part.id] || ''}
            onChange={(e) => handleMarginChange(part.id, e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            size="small"
            sx={{
              width: '100px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: (partMargins[part.id] === null ||
                               partMargins[part.id] === undefined ||
                               partMargins[part.id] === '') ? 'error.main' : 'inherit',
                },
              },
            }}
            error={partMargins[part.id] === null ||
                   partMargins[part.id] === undefined ||
                   partMargins[part.id] === ''}
          />
        </TableCell>
        <TableCell align="right">
          ${(price * quantity).toFixed(2)}
        </TableCell>
      </>
    );
  };

  const renderEstimateSettings = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>Document Settings</Typography>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
        gap: 2,
        mb: 2 
      }}>
        {/* Labor Rate */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            label="Labor Rate"
            type="number"
            value={formatInputValue(laborRate)}
            onChange={(e) => {
              if (order.customer.id !== "9c0d88f5-84f9-454d-833d-a8ced9adad49") {
                const newValue = e.target.value === '' ? 0 : Number(e.target.value);
                setLaborRate(newValue);
                handleRateChange('laborRate', newValue, 150);
              }
            }}
            disabled={order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49"}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            size="small"
            fullWidth
            helperText={order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" ? "Rate locked for this customer" : ""}
          />
          <Tooltip title="Set the labor rate so the estimate total equals the revenue entered on the Accounting tab. Only the labor rate changes — parts, fees, and discounts stay as-is.">
            <span>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={handleSetRateFromRevenue}
                disabled={order.customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49" || !order.revenue}
              >
                Match Revenue{order.revenue ? ` ($${Number(order.revenue).toFixed(2)})` : ''}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Shop Supplies */}
        <TextField
          label="Shop Supplies Rate"
          type="number"
          value={formatInputValue(shopSuppliesRate * 100)}
          onChange={(e) => {
            const newValue = e.target.value === '' ? 0 : Number(e.target.value) / 100;
            setShopSuppliesRate(newValue);
            handleRateChange('shopSuppliesRate', newValue, 0.03);
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          size="small"
          fullWidth
          helperText="Percentage of subtotal"
        />

        {/* Waste Fee */}
        <TextField
          label="Waste Fee"
          type="number"
          value={formatInputValue(wasteFee)}
          onChange={(e) => {
            const newValue = e.target.value === '' ? 0 : Number(e.target.value);
            setWasteFee(newValue);
            handleRateChange('wasteFee', newValue, 50);
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          size="small"
          fullWidth
          helperText="Fixed amount"
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={taxEnabled}
              onChange={(e) => {
                setTaxEnabled(e.target.checked);
                handleRateChange('taxEnabled', e.target.checked, true);
              }}
            />
          }
          label="Enable Tax"
        />
        <Typography variant="body2" color="text.secondary">
          {selectedParts.length === 0 ? "(Tax only applies when parts are included)" : ""}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography variant="h5" gutterBottom>
          {documentType === 'estimate' ? 'Estimate' : 'Invoice'} for Order #{order.stock}
          {documentType === 'flat-invoice' && ' (Flat Rate)'}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleFindDuplicates}
          disabled={isCheckingDuplicates}
          startIcon={isCheckingDuplicates ? <CircularProgress size={18} color="inherit" /> : <ContentCopyIcon />}
          sx={{ flexShrink: 0 }}
        >
          {isCheckingDuplicates ? 'Checking...' : 'Find Duplicates'}
        </Button>
      </Box>

      {/* Customer Information */}
      <Typography variant="h6" gutterBottom>
        Customer: {order.customer?.display_name || 'N/A'}
      </Typography>

      {/* Document Type Toggle */}
      <ToggleButtonGroup
        value={documentType}
        exclusive
        onChange={handleDocumentTypeChange}
        aria-label="document type"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="estimate" aria-label="estimate">Estimate</ToggleButton>
        <ToggleButton value="invoice" aria-label="invoice">Invoice</ToggleButton>
        <ToggleButton value="flat-invoice" aria-label="flat-invoice">Flat Invoice</ToggleButton>
      </ToggleButtonGroup>

      {/* Group services by tag (on-screen view + generated document) */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={groupServicesByTag}
              onChange={(e) => handleGroupServicesToggle(e.target.checked)}
            />
          }
          label="Group services by tag"
        />
        {documentType === 'flat-invoice' && groupServicesByTag && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
            On flat invoices, services are grouped under tag headers without per-tag subtotals.
          </Typography>
        )}
      </Box>

      {/* Add Estimate Settings section here */}
      {renderEstimateSettings()}

      {/* Remove the old labor rate display since it's now in settings */}
      
      {/* Services Section with updated property name */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
          <Typography variant="h6">Services</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Sort alphabetically">
              <span>
                <IconButton
                  size="small"
                  onClick={handleSortServicesAlpha}
                  disabled={!(order.services || []).length}
                >
                  <SortByAlphaIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAddServiceOpen(true)}
              startIcon={<AddCircleOutlineIcon />}
              size="small"
            >
              Add Service
            </Button>
          </Box>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 36 }} />
              <TableCell padding="checkbox">Include</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Group</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          {groupServicesByTag ? (
            // Grouped view: a subheader + subtotal per tag. Re-tag a service via its
            // inline Group cell to move it between groups. (Drag-reorder is available
            // in the ungrouped view.)
            <TableBody>
              {groupedServices.map((group) => {
                const groupSubtotal = group.services
                  .filter(s => selectedServices.some(sel => sel.id === s.id))
                  .reduce((sum, s) => sum + (s.time || 1) * laborRate, 0);
                return (
                  <React.Fragment key={group.tag}>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell />
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>{group.tag}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Subtotal</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>${groupSubtotal.toFixed(2)}</TableCell>
                    </TableRow>
                    {group.services.map((service) => (
                      <TableRow key={service.id}>
                        <TableCell padding="none" sx={{ width: 36 }} />
                        {renderServiceRowCells(service)}
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              {orderedServices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No services found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          ) : (
            <DragDropContext onDragEnd={handleServiceDragEnd}>
              <Droppable droppableId="services-droppable" renderClone={renderServiceClone}>
                {(droppableProvided) => (
                  <TableBody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                    {orderedServices.map((service, index) => (
                      <Draggable key={service.id} draggableId={`service-${service.id}`} index={index}>
                        {(draggableProvided) => (
                          <TableRow
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                          >
                            <TableCell padding="none" sx={{ width: 36 }}>
                              <Box
                                {...draggableProvided.dragHandleProps}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'grab',
                                  color: 'text.disabled',
                                  width: '100%',
                                  height: '100%',
                                  minHeight: 40,
                                  '&:active': { cursor: 'grabbing' },
                                }}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </Box>
                            </TableCell>
                            {renderServiceRowCells(service)}
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Table>
      </TableContainer>

      {/* Updated Parts Section */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
          <Typography variant="h6">Parts</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Sort alphabetically">
              <span>
                <IconButton
                  size="small"
                  onClick={handleSortPartsAlpha}
                  disabled={!(order.parts || []).length}
                >
                  <SortByAlphaIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setAddPartOpen(true)}
              startIcon={<AddCircleOutlineIcon />}
              size="small"
              disabled={!order?.car}
            >
              Add Part
            </Button>
          </Box>
        </Box>
        {/* Group parts by tag (on-screen view + generated document) */}
        <Box sx={{ px: 1, pb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={groupPartsByTag}
                onChange={(e) => handleGroupPartsToggle(e.target.checked)}
              />
            }
            label="Group parts by tag"
          />
          {documentType === 'flat-invoice' && groupPartsByTag && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
              On flat invoices, parts are grouped under tag headers without per-tag subtotals.
            </Typography>
          )}
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 36 }} />
              <TableCell padding="checkbox">Include</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Group</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Margin %</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          {groupPartsByTag ? (
            // Grouped view: a subheader + subtotal per tag. Re-tag a part via its
            // inline Group cell to move it between groups. (Drag-reorder is available
            // in the ungrouped view.)
            <TableBody>
              {groupedParts.map((group) => {
                const groupSubtotal = group.parts
                  .filter(p => selectedParts.some(sel => sel.id === p.id))
                  .reduce((sum, p) => sum + calculatePartPrice(p) * (p.quantity || 1), 0);
                return (
                  <React.Fragment key={group.tag}>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell />
                      <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>{group.tag}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Subtotal</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>${groupSubtotal.toFixed(2)}</TableCell>
                    </TableRow>
                    {group.parts.map((part) => (
                      <TableRow key={part.id}>
                        <TableCell padding="none" sx={{ width: 36 }} />
                        {renderPartRowCells(part)}
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              {orderedParts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No parts found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          ) : (
            <DragDropContext onDragEnd={handlePartDragEnd}>
              <Droppable droppableId="parts-droppable" renderClone={renderPartClone}>
                {(droppableProvided) => (
                  <TableBody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                    {orderedParts.map((part, index) => (
                      <Draggable key={part.id} draggableId={`part-${part.id}`} index={index}>
                        {(draggableProvided) => (
                          <TableRow
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                          >
                            <TableCell padding="none" sx={{ width: 36 }}>
                              <Box
                                {...draggableProvided.dragHandleProps}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'grab',
                                  color: 'text.disabled',
                                  width: '100%',
                                  height: '100%',
                                  minHeight: 40,
                                  '&:active': { cursor: 'grabbing' },
                                }}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </Box>
                            </TableCell>
                            {renderPartRowCells(part)}
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Table>
      </TableContainer>

      {/* Subcontracted Work Section — only shown when the SO has subcontracted
          work. Items are added/managed in the Subcontract tab; here you choose
          which to include and set each margin. Subcontract is non-taxable. */}
      {orderedSubcontracts.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
            <Typography variant="h6">Subcontracted Work</Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Include</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Margin %</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedSubcontracts.map((sub) => {
                const cost = sub.cost || 0;
                const price = calculateSubcontractPrice(sub);
                return (
                  <TableRow key={sub.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSubcontracts.some(s => s.id === sub.id)}
                        onChange={() => handleSubcontractToggle(sub)}
                        disabled={sub.declined}
                      />
                    </TableCell>
                    <TableCell sx={{ textDecoration: sub.declined ? 'line-through' : 'none', color: sub.declined ? 'text.disabled' : 'inherit' }}>
                      {sub.name || '-'}
                      {sub.declined && <Chip label="Declined" color="error" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                    </TableCell>
                    <TableCell align="right">${cost.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={subcontractMargins[sub.id] ?? ''}
                        onChange={(e) => handleSubcontractMarginChange(sub.id, e.target.value)}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        size="small"
                        sx={{ width: '100px' }}
                        disabled={sub.declined}
                      />
                    </TableCell>
                    <TableCell align="right">${price.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Totals Section */}
      <Paper sx={{ p: 2, maxWidth: '400px', ml: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Service and Parts Totals */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', pb: 1 }}>
            <Typography variant="body1">Service Total:</Typography>
            {documentType === 'flat-invoice' ? (
              <TextField
                type="number"
                value={flatServiceTotal !== null ? flatServiceTotal : ''}
                onChange={(e) => handleFlatTotalChange('flatServiceTotal', e.target.value, setFlatServiceTotal)}
                placeholder={calculateServiceTotal().toFixed(2)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                size="small"
                sx={{ width: '140px', '& input': { textAlign: 'right' } }}
              />
            ) : (
              <Typography variant="body1">${calculateServiceTotal().toFixed(2)}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', pb: 1 }}>
            <Typography variant="body1">Parts Total:</Typography>
            {documentType === 'flat-invoice' ? (
              <TextField
                type="number"
                value={flatPartsTotal !== null ? flatPartsTotal : ''}
                onChange={(e) => handleFlatTotalChange('flatPartsTotal', e.target.value, setFlatPartsTotal)}
                placeholder={calculatePartsTotal().toFixed(2)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                size="small"
                sx={{ width: '140px', '& input': { textAlign: 'right' } }}
              />
            ) : (
              <Typography variant="body1">${calculatePartsTotal().toFixed(2)}</Typography>
            )}
          </Box>

          {/* Base Charges */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid #eee',
            borderTop: '1px solid #000',
            pt: 1,
            pb: 1
          }}>
            <Typography variant="body1">Subtotal:</Typography>
            <Typography variant="body1">
              ${(documentType === 'flat-invoice' ? effectiveFlatSubtotal : subtotal).toFixed(2)}
            </Typography>
          </Box>

          {/* Fees */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1">Shop Supplies Fee:</Typography>
            <Typography variant="body1">${shopSuppliesFee.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
            <Typography variant="body1">Waste Fee:</Typography>
            <Typography variant="body1">${wasteFee.toFixed(2)}</Typography>
          </Box>

          {/* Discount */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">Discount:</Typography>
            <TextField
              type="number"
              value={formatInputValue(discount)}
              onChange={(e) => handleDiscountChange(e.target.value)}
              placeholder="0"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              size="small"
              sx={{ 
                width: '120px',
                '& input': { textAlign: 'right' }
              }}
            />
          </Box>

          {/* Adjusted Subtotal */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid #eee',
            borderTop: '1px solid #000',
            py: 1,
            my: 1,
            fontWeight: 'bold'
          }}>
            <Typography variant="body1" fontWeight="bold">Adjusted Subtotal:</Typography>
            <Typography variant="body1" fontWeight="bold">
              ${(documentType === 'flat-invoice' ? flatAdjustedSubtotal : calculateAdjustedSubtotal()).toFixed(2)}
            </Typography>
          </Box>

          {/* Tax Information */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1">Tax Rate:</Typography>
            <Typography variant="body1">{(TAX_RATE * 100).toFixed(1)}%</Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid #eee',
            pb: 1
          }}>
            <Typography variant="body1">Total Tax:</Typography>
            <Typography variant="body1">
              ${(documentType === 'flat-invoice' ? flatTax : tax).toFixed(2)}
            </Typography>
          </Box>

          {/* Subcontracted work — non-taxable, added after tax */}
          {calculateSubcontractTotal() > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1">Subcontract Total:</Typography>
              <Typography variant="body1">${calculateSubcontractTotal().toFixed(2)}</Typography>
            </Box>
          )}

          {/* Payments */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1">Payments:</Typography>
            <Typography variant="body1" color="error">-${calculateTotalPayments().toFixed(2)}</Typography>
          </Box>

          {/* Final Balance */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '2px solid #000',
            mt: 2,
            pt: 2
          }}>
            <Typography variant="h6" fontWeight="bold">Balance Due:</Typography>
            <Typography variant="h6" fontWeight="bold">
              ${((documentType === 'flat-invoice' ? flatTotal : total + calculateSubcontractTotal()) - calculateTotalPayments()).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Document actions, in the order they get used: build it, send it, track it. */}
      <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              1. Build
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateEstimate}
              disabled={invalidMargins || isGeneratingEstimate}
              startIcon={isGeneratingEstimate ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfOutlinedIcon />}
            >
              {isGeneratingEstimate ? 'Generating...' : `Generate ${documentTypeLabel}`}
            </Button>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              2. Send to customer
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Tooltip title={`Email the ${docLabel().toLowerCase()} as a PDF. No signature requested.`}>
                <Button
                  variant="outlined"
                  startIcon={<EmailOutlinedIcon />}
                  onClick={() => setSendDocOpen(true)}
                  disabled={invalidMargins || isGeneratingEstimate}
                >
                  Email only
                </Button>
              </Tooltip>
              <Tooltip title="Email a signing link. The customer gets the signed copy when they finish.">
                <Button
                  variant="outlined"
                  startIcon={<DrawOutlinedIcon />}
                  onClick={sendServiceForSignature}
                  disabled={invalidMargins || isGeneratingEstimate}
                >
                  Request Signature
                </Button>
              </Tooltip>
              <Tooltip title="Open a signing link here for a customer standing in front of you.">
                <Button
                  variant="outlined"
                  startIcon={<TabletMacOutlinedIcon />}
                  onClick={signServiceInPerson}
                  disabled={invalidMargins || isGeneratingEstimate}
                >
                  Sign in Person
                </Button>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                3. Track
              </Typography>
              <Button variant="text" startIcon={<FactCheckOutlinedIcon />} onClick={() => setSigOpen(true)}>
                Signature Status
              </Button>
            </Box>
            <Tooltip title="More document actions">
              <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
        <MenuItem
          onClick={() => { setMoreAnchor(null); previewServicePlacement(); }}
          disabled={isGeneratingEstimate}
        >
          Preview signature placement
        </MenuItem>
      </Menu>

      <SendServiceDocumentDialog
        open={sendDocOpen}
        onClose={() => setSendDocOpen(false)}
        docLabel={docLabel()}
        stock={order.stock}
        defaultTo={order.customer?.email || ''}
        onSend={emailServiceDocument}
      />

      <SignatureStatusDialog
        open={sigOpen}
        onClose={() => setSigOpen(false)}
        car={{ stock: order.stock }}
        table="orders"
        type="service"
        label={docLabel()}
      />

      <InPersonSignDialog
        open={inPersonOpen}
        onClose={() => setInPersonOpen(false)}
        signUrl={inPersonUrl}
        signerName={inPersonSigner}
      />

      <Snackbar
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add Service Dialog */}
      <AddServiceOrderDialog 
        serviceOrderId={order.stock} 
        open={addServiceOpen} 
        onClose={() => setAddServiceOpen(false)} 
        callback={updateSO} 
      />

      {/* Add Part Dialog */}
      <OrderPartDialog 
        open={addPartOpen} 
        onClose={() => setAddPartOpen(false)} 
        order={{ 
          id: order.stock, 
          status: order.status,
          car_title: order?.car?.title || order?.car?.stock || 'Vehicle' 
        }}
        onSuccess={handlePartAdded}
      />

      {/* AI duplicate review dialog */}
      <DuplicatesDialog
        open={duplicatesOpen}
        onClose={() => setDuplicatesOpen(false)}
        groups={duplicateGroups}
        order={order}
      />
    </Paper>
  );
}



const isValidNumber = (value) => {
  return !(value === null || value === undefined || value === '');
}

const isExcludedService = (s) => !!s.declined;
const isExcludedPart = (p) => !!p.declined || ['declined', 'returning', 'returned'].includes(p.status);

const STATUS_ORDER = { pending: 0, approved: 1, inbound: 2, complete: 3, returning: 4, returned: 5 };
const byStatus = (a, b) => (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0);

// Sort items by a saved id order; items not in the order list keep their byStatus order and append after.
const applyCustomOrder = (items, idOrder) => {
  if (!items) return [];
  if (!idOrder || !idOrder.length) return [...items].sort(byStatus);
  const indexById = new Map(idOrder.map((id, i) => [id, i]));
  const known = items.filter(i => indexById.has(i.id)).sort((a, b) => indexById.get(a.id) - indexById.get(b.id));
  const unknown = items.filter(i => !indexById.has(i.id)).sort(byStatus);
  return [...known, ...unknown];
};

